import React, { useEffect, useMemo, useState } from "react";
import { Alert, Col, Form, Row, Spinner } from "react-bootstrap";
import { BsCheck2Circle, BsPlus, BsSave, BsStarFill, BsTrash } from "react-icons/bs";
import DashboardLayout from "../components/DashboardLayout";
import CertificateAssetPicker from "../components/CertificateAssetPicker";
import CertificateFooter from "../components/CertificateFooter";
import FooterEditor, { footerToLogos } from "../components/FooterEditor";
import { useAuth } from "../context/userAuth/UserAuthState";
import {
  deleteCertificateAssetAPI,
  deleteCertificateFooterAPI,
  fetchCertificateConfigAPI,
  saveCertificateFooterAPI,
  updateCertificateDefaultsAPI,
  TEMPLATE_ASSETS_BASE_URL,
} from "../api/api";

const isSuperRole = (role) => role === "isAdmin" || role === "super_admin" || role === "pro_super_admin";

const emptyFooterForm = { id: "", name: "", companies: [], website: "", email: "", gmail: "" };

/**
 * Shared certificate configuration used by every template: default
 * Inspected By / Approved By / Company Seal, plus the saved footer strips.
 */
const CertificateConfigurationPage = () => {
  const { user } = useAuth();
  const token = user?.authorization;
  const canDelete = isSuperRole(user?.user?.is_user);

  const [assets, setAssets] = useState([]);
  const [footers, setFooters] = useState([]);
  const [defaults, setDefaults] = useState({ inspected_by_asset_id: "", approved_by_asset_id: "", company_seal_asset_id: "", footer_id: "" });
  const [signOff, setSignOff] = useState({ inspected_by_asset_id: "", approved_by_asset_id: "", company_seal_asset_id: "" });
  const [footerForm, setFooterForm] = useState(emptyFooterForm);
  const [loading, setLoading] = useState(true);
  const [savingSignOff, setSavingSignOff] = useState(false);
  const [savingFooter, setSavingFooter] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const signatures = useMemo(() => assets.filter((asset) => asset.asset_type === "signature"), [assets]);
  const seals = useMemo(() => assets.filter((asset) => asset.asset_type === "seal"), [assets]);
  const footerAssets = useMemo(() => assets.filter((asset) => asset.asset_type === "footer"), [assets]);

  const footerToForm = (footer) => ({
    id: footer.id,
    name: footer.name,
    companies: (footer.companies || []).map((company) => ({ ...company })),
    website: footer.website || "",
    email: footer.email || "",
    gmail: footer.gmail || "",
  });

  useEffect(() => {
    const load = async () => {
      const res = await fetchCertificateConfigAPI(token);
      setLoading(false);
      if (!res.success) {
        setError(res.message || "Failed to load configuration.");
        return;
      }
      setAssets(res.assets || []);
      setFooters(res.footers || []);
      setDefaults(res.defaults);
      setSignOff({
        inspected_by_asset_id: res.defaults.inspected_by_asset_id,
        approved_by_asset_id: res.defaults.approved_by_asset_id,
        company_seal_asset_id: res.defaults.company_seal_asset_id,
      });
      const current = (res.footers || []).find((footer) => String(footer.id) === String(res.defaults.footer_id)) || res.footers?.[0];
      if (current) setFooterForm(footerToForm(current));
    };
    load();
  }, [token]);

  const notify = (text) => {
    setError("");
    setMessage(text);
  };

  const fail = (text) => {
    setMessage("");
    setError(text);
  };

  const handleAssetCreated = (asset) => setAssets((prev) => [...prev, asset]);

  // --- sign-off defaults ----------------------------------------------------

  const saveSignOffDefaults = async () => {
    setSavingSignOff(true);
    const res = await updateCertificateDefaultsAPI(signOff, token);
    setSavingSignOff(false);
    if (res.success) {
      setDefaults(res.defaults);
      notify("Default Inspected By, Approved By and Company Seal saved. New certificates start with these.");
    } else {
      fail(res.message || "Failed to save defaults.");
    }
  };

  const removeAsset = async (asset) => {
    if (!window.confirm(`Delete "${asset.name}"?`)) return;
    const res = await deleteCertificateAssetAPI({ id: asset.id }, token);
    if (res.success) {
      setAssets((prev) => prev.filter((item) => item.id !== asset.id));
      notify(`"${asset.name}" deleted.`);
    } else {
      fail(res.message || "Failed to delete.");
    }
  };

  const isDefaultAsset = (asset) =>
    [defaults.inspected_by_asset_id, defaults.approved_by_asset_id, defaults.company_seal_asset_id]
      .some((id) => String(id) === String(asset.id));

  const assetList = (title, list) => (
    <div className="cfg-asset-list">
      <h6 className="cc-form-section">{title}</h6>
      <div className="cfg-asset-grid">
        {list.map((asset) => (
          <div className="cfg-asset" key={asset.id}>
            <img src={asset.image_url} alt={asset.name} />
            <span>{asset.name}</span>
            {isDefaultAsset(asset) && <em title="Currently a default"><BsStarFill size={11} /> default</em>}
            {canDelete && !asset.is_default && !isDefaultAsset(asset) && (
              <button type="button" className="cp-delete-btn" title="Delete" onClick={() => removeAsset(asset)}>
                <BsTrash size={12} />
              </button>
            )}
          </div>
        ))}
        {!list.length && <span className="text-muted">Nothing saved yet.</span>}
      </div>
    </div>
  );

  // --- footer ---------------------------------------------------------------

  const selectFooter = (id) => {
    const footer = footers.find((item) => String(item.id) === String(id));
    if (footer) setFooterForm(footerToForm(footer));
  };

  const patchFooter = (patch) => setFooterForm((prev) => ({ ...prev, ...patch }));

  const saveFooter = async (asNew) => {
    if (!footerForm.name.trim()) {
      fail("Footer name is required.");
      return;
    }
    setSavingFooter(true);
    const body = { ...footerForm, id: asNew ? undefined : footerForm.id };
    const res = await saveCertificateFooterAPI(body, token);
    setSavingFooter(false);
    if (!res.success) {
      fail(res.message || "Failed to save footer.");
      return;
    }
    setFooters((prev) => {
      const exists = prev.some((footer) => footer.id === res.footer.id);
      return exists ? prev.map((footer) => (footer.id === res.footer.id ? res.footer : footer)) : [...prev, res.footer];
    });
    setFooterForm(footerToForm(res.footer));
    notify(asNew ? `Footer "${res.footer.name}" saved.` : `Footer "${res.footer.name}" updated. Certificates using it will show the new logos and contacts.`);
  };

  const makeFooterDefault = async () => {
    const res = await updateCertificateDefaultsAPI({ footer_id: footerForm.id }, token);
    if (res.success) {
      setDefaults(res.defaults);
      notify(`"${footerForm.name}" is now the default footer for all templates.`);
    } else {
      fail(res.message || "Failed to set default footer.");
    }
  };

  const removeFooter = async () => {
    if (!window.confirm(`Delete footer "${footerForm.name}"?`)) return;
    const res = await deleteCertificateFooterAPI({ id: footerForm.id }, token);
    if (res.success) {
      const remaining = footers.filter((footer) => footer.id !== footerForm.id);
      setFooters(remaining);
      const next = remaining.find((footer) => String(footer.id) === String(defaults.footer_id)) || remaining[0];
      setFooterForm(next ? footerToForm(next) : emptyFooterForm);
      notify("Footer deleted.");
    } else {
      fail(res.message || "Failed to delete footer.");
    }
  };

  const selectedFooter = footers.find((footer) => String(footer.id) === String(footerForm.id));
  const footerIsDefault = String(defaults.footer_id) === String(footerForm.id);

  return (
    <DashboardLayout pageTitle="Configuration">
      <div className="cp-header">
        <div>
          <h2 className="cp-header__title">Certificate Configuration</h2>
          <p className="cp-header__sub">Defaults shared by every certificate template. The header (logo, title and bar) is fixed.</p>
        </div>
      </div>

      {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}
      {message && <Alert variant="success" onClose={() => setMessage("")} dismissible>{message}</Alert>}

      {loading ? (
        <Spinner animation="border" size="sm" />
      ) : (
        <>
          <div className="ac-form-card mb-4">
            <h3 className="db-card__title">Default Sign-off</h3>
            <p className="cc-part__hint mb-3">
              Pre-selected on every new certificate. Add more people or seals with Upload / Draw; they appear in the template dropdowns too.
            </p>
            <Row>
              <Col md={4}>
                <CertificateAssetPicker
                  label="Inspected By"
                  assetType="signature"
                  assets={signatures}
                  value={signOff.inspected_by_asset_id}
                  onChange={(asset) => setSignOff((prev) => ({ ...prev, inspected_by_asset_id: asset ? asset.id : "" }))}
                  onAssetCreated={handleAssetCreated}
                  token={token}
                  allowDraw
                />
              </Col>
              <Col md={4}>
                <CertificateAssetPicker
                  label="Approved By"
                  assetType="signature"
                  assets={signatures}
                  value={signOff.approved_by_asset_id}
                  onChange={(asset) => setSignOff((prev) => ({ ...prev, approved_by_asset_id: asset ? asset.id : "" }))}
                  onAssetCreated={handleAssetCreated}
                  token={token}
                  allowDraw
                />
              </Col>
              <Col md={4}>
                <CertificateAssetPicker
                  label="Company Seal"
                  assetType="seal"
                  assets={seals}
                  value={signOff.company_seal_asset_id}
                  onChange={(asset) => setSignOff((prev) => ({ ...prev, company_seal_asset_id: asset ? asset.id : "" }))}
                  onAssetCreated={handleAssetCreated}
                  token={token}
                />
              </Col>
            </Row>
            <button
              type="button"
              className="cp-modal-submit"
              onClick={saveSignOffDefaults}
              disabled={savingSignOff || !signOff.inspected_by_asset_id || !signOff.approved_by_asset_id || !signOff.company_seal_asset_id}
            >
              {savingSignOff ? <Spinner size="sm" animation="border" /> : <><BsCheck2Circle /> Save as defaults</>}
            </button>

            {assetList("Saved signatures (Inspected By / Approved By)", signatures)}
            {assetList("Saved company seals", seals)}
          </div>

          <div className="ac-form-card">
            <h3 className="db-card__title">Footer</h3>
            <p className="cc-part__hint mb-3">
              Seven accreditation logos with their websites, plus the contact lines. Pick a saved footer to edit it, or save your changes as a new one.
            </p>
            <Row className="align-items-end">
              <Col md={5}>
                <Form.Group className="mb-3">
                  <Form.Label className="cp-form-label">Saved footer</Form.Label>
                  <Form.Select className="cp-form-control" value={footerForm.id || ""} onChange={(e) => selectFooter(e.target.value)}>
                    {footers.map((footer) => (
                      <option key={footer.id} value={footer.id}>
                        {footer.name}{String(footer.id) === String(defaults.footer_id) ? " (default)" : ""}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label className="cp-form-label">Footer name</Form.Label>
                  <Form.Control className="cp-form-control" value={footerForm.name} onChange={(e) => patchFooter({ name: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={3} className="mb-3 d-flex gap-2">
                {footerForm.id && !footerIsDefault && (
                  <button type="button" className="cp-edit-btn" onClick={makeFooterDefault}>
                    <BsStarFill size={12} /> Set as default
                  </button>
                )}
                {footerForm.id && canDelete && selectedFooter && !selectedFooter.is_default && !footerIsDefault && (
                  <button type="button" className="cp-delete-btn" title="Delete footer" onClick={removeFooter}>
                    <BsTrash size={13} />
                  </button>
                )}
              </Col>
            </Row>

            <FooterEditor
              value={footerForm}
              onChange={patchFooter}
              footerAssets={footerAssets}
              token={token}
              onAssetCreated={handleAssetCreated}
              onError={fail}
            />

            <h6 className="cc-form-section">Live view</h6>
            <div className="cc-preview cfg-footer-preview">
              <div className="cfg-footer-stage">
                <CertificateFooter
                  assetsBase={TEMPLATE_ASSETS_BASE_URL}
                  logos={footerToLogos(footerForm.companies, footerAssets)}
                  website={footerForm.website}
                  email={footerForm.email}
                  gmail={footerForm.gmail}
                />
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mt-3">
              <button type="button" className="cp-modal-submit" onClick={() => saveFooter(false)} disabled={savingFooter || !footerForm.id}>
                {savingFooter ? <Spinner size="sm" animation="border" /> : <><BsSave /> Save changes</>}
              </button>
              <button type="button" className="cp-edit-btn" onClick={() => saveFooter(true)} disabled={savingFooter}>
                <BsPlus size={14} /> Save as new footer
              </button>
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
};

export default CertificateConfigurationPage;
